import { type inferRouterOutputs } from "@trpc/server";
import { type AppRouter } from "@server/api/root";
import {
  type PayloadAction,
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";
import { trpc } from "@utils/api";

export type LogEntry =
  inferRouterOutputs<AppRouter>["logs"]["getExecutableLogs"]["logs"][number];
export type ExecutableInfo =
  inferRouterOutputs<AppRouter>["logs"]["getExecutables"][number];

export interface LogsState {
  executables: {
    isLoading: boolean;
    data: ExecutableInfo[];
    error: string | null;
  };
  logs: {
    isLoading: boolean;
    data: LogEntry[];
    nextToken: string | null;
    error: string | null;
  };
  filters: {
    executableId: string | null;
    appId: string | null;
    appVersionId: string | null;
    startTime: string | null;
    endTime: string | null;
  };
  autoRefresh: boolean;
}

const initialState: LogsState = {
  executables: {
    isLoading: false,
    data: [],
    error: null,
  },
  logs: {
    isLoading: false,
    data: [],
    nextToken: null,
    error: null,
  },
  filters: {
    executableId: null,
    appId: null,
    appVersionId: null,
    startTime: null,
    endTime: null,
  },
  autoRefresh: true,
};

export const fetchExecutables = createAsyncThunk(
  "logs/fetchExecutables",
  async (input: { appId?: string } | undefined, { rejectWithValue }) => {
    try {
      const result = await trpc.logs.getExecutables.query(input);
      return result;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to fetch executables",
      );
    }
  },
);

export const fetchLogs = createAsyncThunk(
  "logs/fetchLogs",
  async (
    input: {
      executableId?: string;
      appId?: string;
      appVersionId?: string;
      startTime?: Date;
      endTime?: Date;
      limit?: number;
      nextToken?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const result = await trpc.logs.getExecutableLogs.query(input);
      return result;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to fetch logs",
      );
    }
  },
);

const logsSlice = createSlice({
  name: "logs",
  initialState,
  reducers: {
    setExecutableFilter: (state, action: PayloadAction<string | null>) => {
      state.filters.executableId = action.payload;
    },
    setAppFilter: (state, action: PayloadAction<string | null>) => {
      state.filters.appId = action.payload;
    },
    setAppVersionFilter: (state, action: PayloadAction<string | null>) => {
      state.filters.appVersionId = action.payload;
    },
    setTimeRange: (
      state,
      action: PayloadAction<{
        startTime: string | null;
        endTime: string | null;
      }>,
    ) => {
      state.filters.startTime = action.payload.startTime;
      state.filters.endTime = action.payload.endTime;
    },
    toggleAutoRefresh: (state) => {
      state.autoRefresh = !state.autoRefresh;
    },
    setAutoRefresh: (state, action: PayloadAction<boolean>) => {
      state.autoRefresh = action.payload;
    },
    clearLogs: (state) => {
      state.logs.data = [];
      state.logs.nextToken = null;
      state.logs.error = null;
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    appendLogs: (state, action: PayloadAction<LogEntry[]>) => {
      const existingIds = new Set(
        state.logs.data.map((log) => `${log.timestamp}-${log.message}`),
      );
      const newLogs = action.payload.filter(
        (log) => !existingIds.has(`${log.timestamp}-${log.message}`),
      );
      state.logs.data = [...state.logs.data, ...newLogs];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchExecutables.pending, (state) => {
        state.executables.isLoading = true;
        state.executables.error = null;
      })
      .addCase(fetchExecutables.fulfilled, (state, action) => {
        state.executables.isLoading = false;
        state.executables.data = action.payload;
      })
      .addCase(fetchExecutables.rejected, (state, action) => {
        state.executables.isLoading = false;
        state.executables.error = action.payload as string;
      })
      .addCase(fetchLogs.pending, (state) => {
        state.logs.isLoading = true;
        state.logs.error = null;
      })
      .addCase(fetchLogs.fulfilled, (state, action) => {
        state.logs.isLoading = false;
        state.logs.data = action.payload.logs;
        state.logs.nextToken = action.payload.nextToken ?? null;
      })
      .addCase(fetchLogs.rejected, (state, action) => {
        state.logs.isLoading = false;
        state.logs.error = action.payload as string;
      });
  },
});

export const {
  setExecutableFilter,
  setAppFilter,
  setAppVersionFilter,
  setTimeRange,
  toggleAutoRefresh,
  setAutoRefresh,
  clearLogs,
  clearFilters,
  appendLogs,
} = logsSlice.actions;

export default logsSlice.reducer;
